import React from 'react';
import { NavLink } from 'react-router-dom';

import { useViewMode } from '../hooks/useViewMode';
import { useAuth } from '../hooks/useAuth';
import '../styles/layout.css';

type NavGroupItem = {
  to: string;
  label: string;
  viewMode?: 'agent' | 'insured';
  end?: boolean;
  hidden?: boolean;
};

type NavGroup = {
  id: string;
  label: string;
  icon: string;
  items: NavGroupItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'pi pi-home',
    items: [
      { to: '/agentdashboard', label: 'Agent Dashboard', viewMode: 'agent' },
      // { to: '/insureddashboard', label: 'Insured Dashboard', viewMode: 'insured' }
    ]
  },
  {
    id: 'searchclient',
    label: 'Search Customer',
    icon: 'pi pi-search',
    items: [
      { to: '/selectclient', label: 'Search Customer' }
    ]
  },
  {
    id: 'certificateholders',
    label: 'Certificate Holders',
    icon: 'pi pi-users',
    items: [
      { to: '/certificateholders', label: 'Certificate Holders' }
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'pi pi-chart-bar',
    items: [
      { to: '/reports', label: 'Dashboard', end: true },
      { to: '/reports/activity', label: 'Activity Report' }
    ]
  },
  {
    id: 'templates',
    label: 'Customization',
    icon: 'pi pi-cog',
    items: [
      { to: '/email-templates', label: 'Email Templates' },
      { to: '/signature', label: 'Signatures' }
    ]
  }
];

export type SideNavProps = {
  collapsed: boolean;
  onNavLinkClick?: () => void;
  onExpandRequest?: () => void;
};

export default function SideNav({ collapsed, onNavLinkClick, onExpandRequest }: SideNavProps) {
  const { setViewMode } = useViewMode();
  const { hasReportsAccess } = useAuth();
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(() =>
    NAV_GROUPS.reduce<Record<string, boolean>>((acc, group) => {
      acc[group.id] = true;
      return acc;
    }, {})
  );

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <nav className={collapsed ? 'app-sidenav app-sidenav--collapsed' : 'app-sidenav'} aria-label="Primary">
      <div className="app-sidenav__inner">
        {/* <div className="app-sidenav__sectionLabel">Navigation</div> */}
        <ul className="app-sidenav__groupList">
          {NAV_GROUPS.filter(g => g.id !== 'reports' || hasReportsAccess).map((group) => {
            const isOpen = openGroups[group.id];
            const isSingleItem = group.items.length === 1;
            return (
              <li key={group.id} className="app-sidenav__group">
                {collapsed && isSingleItem ? (
                  <NavLink
                    to={group.items[0].to}
                    onClick={() => {
                      if (group.items[0].viewMode) setViewMode(group.items[0].viewMode);
                      onNavLinkClick?.();
                    }}
                    className={({ isActive }) =>
                      isActive
                        ? 'app-sidenav__groupHeader app-sidenav__groupHeader--active'
                        : 'app-sidenav__groupHeader'
                    }
                    title={group.label}
                  >
                    <span className="app-sidenav__groupMain">
                      <i className={`app-sidenav__groupIcon ${group.icon}`} aria-hidden="true" />
                    </span>
                  </NavLink>
                ) : collapsed ? (
                  <button
                    type="button"
                    className="app-sidenav__groupHeader"
                    onClick={() => {
                      if (onExpandRequest) {
                        onExpandRequest();
                        // Open this group when expanding
                        setOpenGroups((prev) => ({ ...prev, [group.id]: true }));
                      }
                    }}
                    title={group.label}
                  >
                    <span className="app-sidenav__groupMain">
                      <i className={`app-sidenav__groupIcon ${group.icon}`} aria-hidden="true" />
                    </span>
                  </button>
                ) : isSingleItem ? (
                  <NavLink
                    to={group.items[0].to}
                    onClick={() => {
                      if (group.items[0].viewMode) setViewMode(group.items[0].viewMode);
                      onNavLinkClick?.();
                    }}
                    className={({ isActive }) =>
                      isActive
                        ? 'app-sidenav__groupHeader app-sidenav__groupHeader--active'
                        : 'app-sidenav__groupHeader'
                    }
                  >
                    <span className="app-sidenav__groupMain">
                      <i className={`app-sidenav__groupIcon ${group.icon}`} aria-hidden="true" />
                      <span className="app-sidenav__groupLabel">{group.label}</span>
                    </span>
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className="app-sidenav__groupHeader"
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className="app-sidenav__groupMain">
                      <i className={`app-sidenav__groupIcon ${group.icon}`} aria-hidden="true" />
                      <span className="app-sidenav__groupLabel">{group.label}</span>
                    </span>
                    <i
                      className={
                        isOpen
                          ? 'pi pi-chevron-down app-sidenav__groupChevron'
                          : 'pi pi-chevron-right app-sidenav__groupChevron'
                      }
                      aria-hidden="true"
                    />
                  </button>
                )}
                {isOpen && !collapsed && !isSingleItem && (
                  <ul className="app-sidenav__subList">
                    {group.items.filter(item => !item.hidden).map((item) => (
                      <li key={item.to} className="app-sidenav__subItem">
                        <NavLink
                          to={item.to}
                          end={item.end}
                          onClick={() => {
                            if (item.viewMode) {
                              setViewMode(item.viewMode);
                            }
                            onNavLinkClick?.();
                          }}
                          className={({ isActive }) =>
                            isActive
                              ? 'app-sidenav__subLink app-sidenav__subLink--active'
                              : 'app-sidenav__subLink'
                          }
                        >
                          <span className="app-sidenav__subText">{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
